import { useState } from 'react'
import { getCardById, MAJOR_ARCANA } from '@lunary/tarot-data'
import type { UserActivityResponseDto } from '@lunary/api-client'
import { ACTION_LABELS, ORDER_STATUS_LABELS, formatDate, formatDateTime, roleLabel, sourceLabel } from './labels'
import styles from './user-card.module.css'

const TABS = [
  { key: 'daily', label: 'Карта дня' },
  { key: 'orders', label: 'Личный аркан' },
  { key: 'feedback', label: 'Отзывы' },
  { key: 'log', label: 'Журнал' },
] as const

type TabKey = (typeof TABS)[number]['key']

type Item = { readonly id: string; readonly title: string; readonly meta: string; readonly note?: string | null }

function itemsFor(tab: TabKey, a: UserActivityResponseDto): Item[] {
  switch (tab) {
    case 'daily':
      return a.daily_readings.map((r) => ({
        id: r.id,
        title: getCardById(r.card_id)?.name ?? r.card_id,
        meta: `${formatDate(r.publish_date)} · трактовка ${formatDateTime(r.created_at)}`,
      }))
    case 'orders':
      return a.arcana_orders.map((o) => ({
        id: o.id,
        title: `${o.arcana_id} · ${MAJOR_ARCANA.find((c) => c.number === o.arcana_id)?.name ?? ''}`,
        meta: `${ORDER_STATUS_LABELS[o.status] ?? o.status} · ${sourceLabel(o.source)} · ${formatDateTime(o.requested_at)}`,
      }))
    case 'feedback':
      return a.feedback.map((f) => ({
        id: f.id,
        title: f.kind === 'like' ? 'Понравилось' : 'Не понравилось',
        meta: formatDateTime(f.created_at),
        note: f.comment,
      }))
    case 'log':
      return [
        ...a.role_changes.map((c) => ({
          id: c.id,
          at: c.created_at,
          title: `Роль: ${roleLabel(c.previous_role)} → ${roleLabel(c.new_role)}`,
          meta: `${c.actor_name ?? '—'} · ${formatDateTime(c.created_at)}`,
        })),
        ...a.admin_actions.map((x) => ({
          id: x.id,
          at: x.created_at,
          title: ACTION_LABELS[x.action] ?? x.action,
          meta: `${x.actor_name ?? '—'} · ${formatDateTime(x.created_at)}`,
          note: x.reason,
        })),
      ].sort((l, r) => r.at.localeCompare(l.at))
  }
}

function countFor(tab: TabKey, a: UserActivityResponseDto): number {
  if (tab === 'daily') return a.daily_readings.length
  if (tab === 'orders') return a.arcana_orders.length
  if (tab === 'feedback') return a.feedback.length
  return a.role_changes.length + a.admin_actions.length
}

export function UserHistory({ activity }: { activity: UserActivityResponseDto | undefined }) {
  const [tab, setTab] = useState<TabKey>('daily')
  const items = activity ? itemsFor(tab, activity) : []

  return (
    <section className="card-panel" aria-labelledby="user-history-title">
      <h2 id="user-history-title">История</h2>
      <div className={styles.tabs} role="tablist" aria-label="Разделы истории">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            id={`tab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls="user-history-panel"
            className={tab === t.key ? styles.tabActive : styles.tab}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {activity && <span className={styles.count}>{countFor(t.key, activity)}</span>}
          </button>
        ))}
      </div>
      <div id="user-history-panel" role="tabpanel" aria-labelledby={`tab-${tab}`}>
        {!activity && <div className="spinner" aria-label="Загрузка" />}
        {activity && items.length === 0 && <p className="muted">Пока пусто.</p>}
        <ul className={styles.list}>
          {items.map((item) => (
            <li key={item.id} className={styles.item}>
              <div>{item.title}</div>
              <div className="muted">{item.meta}</div>
              {item.note && <div className={styles.note}>{item.note}</div>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
